import { useEffect, useRef, useState } from "react";
// DISABLED (for now): ChevronDown + Clock — only used by the hidden "Snooze this thread" row.
import { BotOff, PanelRightClose, Plus } from "lucide-react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { AiHandlingControl } from "@/components/messages/ai-handling-control";
import { RightPanelTabs } from "@/components/messages/right-panel-tabs";
// DISABLED (for now): manual Status section + "Snooze this thread" row — in the current
// logic the workflow status is derived automatically, so the panel keeps AI handling +
// checklist only.
// import { ThreadStatusControl } from "@/components/messages/thread-status-control";
// import { SnoozeMenu } from "@/components/messages/snooze-menu";
import { TaskCard } from "@/components/messages/task-card";
// DISABLED (for now): thread assignment — assignment lives on tasks only.
// import { getThreadAssignee } from "@/lib/thread-assignee";
import { getThreadFlagStrings, getThreadLockReason, humanizeLockReason } from "@/lib/thread-flags";
// import { getAuthUser } from "@/lib/api/token-store";
import { cn } from "@/lib/utils";

function panelSectionLabel(children) {
  return (
    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * The right-hand "Tasks" panel (design 02-thread-tasks): AI handling and the thread
 * checklist (the manual status/snooze sections are DISABLED for now). Shares the expanded
 * right panel with Participants via the tab strip on top (`onShowParticipants` switches tabs).
 */
export function ThreadTasksPanel({
  thread,
  listClockMs,
  tasks = [],
  tasksLoading = false,
  onClose,
  onShowParticipants,
  onThreadFlagChange,
  onClearAiHandling,
  // DISABLED (for now): manual status override + snooze — the Status/Snooze sections are hidden.
  // onStatusChange,
  // DISABLED (for now): thread claim/release — assignment lives on tasks only.
  // onClaimThread,
  // onReleaseThread,
  // onSnoozeThread,
  onTaskCreate,
  onTaskToggle,
  onTaskClaim,
  onTaskAssign,
  onTaskDelete,
  // Thread team members ({ id, label, avatarUrl }) offered by the per-task assign picker.
  assignableUsers = [],
}) {
  const tid = String(thread?.threadId || thread?.id || "").trim();
  const nowMs = listClockMs ?? Date.now();
  const flags = getThreadFlagStrings(thread);
  const isLocked = flags.includes("locked");
  const lockReason = getThreadLockReason(thread);
  // DISABLED (for now): staff_handles is retired and no longer shown as paused.
  // const isStaffHandles = flags.includes("staff_handles");
  // DISABLED (for now): the thread "Owned by …" box — thread assignment is hidden.
  // const assignee = getThreadAssignee(thread);
  // const me = String(getAuthUser()?.id || "").trim();
  // const assigneeIsMe = Boolean(assignee && me && assignee.id === me);
  // const claimedAtLabel = thread?.assignedAt
  //   ? new Date(thread.assignedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  //   : "";

  const openTasks = tasks.filter((t) => t.status !== "done");
  const doneTasks = tasks.filter((t) => t.status === "done");
  const [hideDone, setHideDone] = useState(false);

  // "Add a task…" inline input.
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const addInputRef = useRef(null);
  useEffect(() => {
    if (adding) addInputRef.current?.focus();
  }, [adding]);
  const submitNewTask = () => {
    const title = newTitle.trim();
    if (title) void onTaskCreate?.(title);
    setNewTitle("");
    setAdding(false);
  };

  if (!tid) return null;

  return (
    <aside
      className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-border/80 bg-muted/20 shadow-[inset_1px_0_0_0_hsl(var(--border)/0.5)] dark:bg-muted/15"
      aria-label="Tasks"
    >
      <RightPanelTabs
        active="tasks"
        tasksBadge={openTasks.length}
        onChange={(tab) => {
          if (tab === "participants") onShowParticipants?.();
        }}
        trailing={
          <button
            type="button"
            aria-label="Hide panel"
            title="Hide panel"
            onClick={onClose}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <PanelRightClose className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          </button>
        }
      />

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 px-4 py-4">
          <section>
            {panelSectionLabel("AI handling")}
            <AiHandlingControl
              thread={thread}
              onThreadFlagChange={onThreadFlagChange}
              onClearAiHandling={onClearAiHandling}
            />
            {/* DISABLED (for now): staff_handles no longer triggers this note — locked only. */}
            {isLocked ? (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-950/30">
                <BotOff
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
                  strokeWidth={2.25}
                  aria-hidden
                />
                <p className="text-[11px] leading-snug text-amber-900 dark:text-amber-100">
                  <span className="font-semibold">AI paused</span>
                  {lockReason ? (
                    <>
                      {" — auto-locked from "}
                      <span className="font-mono text-[10px]">{humanizeLockReason(lockReason)}</span>.
                    </>
                  ) : (
                    " — a human owns the replies."
                  )}{" "}
                  The bot won’t reply until a human unlocks.
                </p>
              </div>
            ) : null}
          </section>

          {/* DISABLED (for now): the manual Status section (Open | Pending | Done) and the
              "Snooze this thread" row — in the current logic the workflow status is derived
              automatically, so the panel keeps AI handling + checklist only.
          <section>
            {panelSectionLabel("Status")}
            <ThreadStatusControl
              thread={thread}
              listClockMs={nowMs}
              onStatusChange={onStatusChange}
              onSnoozeThread={onSnoozeThread}
            />
            DISABLED (for now): the thread "Owned by …" box + Release — thread
                assignment is hidden; assignment lives on tasks only.
            {assignee ? (
              <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2 dark:border-indigo-500/30 dark:bg-indigo-950/30">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                      assigneeIsMe
                        ? "bg-indigo-600 text-white dark:bg-indigo-500"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {(assignee.name || "?")
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((p) => p[0]?.toUpperCase())
                      .join("") || "?"}
                  </span>
                  <div className="min-w-0 leading-tight">
                    <p className="truncate text-xs font-semibold text-indigo-800 dark:text-indigo-200">
                      {assigneeIsMe ? "Owned by you" : `Owned by ${assignee.name}`}
                    </p>
                    <p className="truncate text-[10px] text-indigo-600/70 dark:text-indigo-300/70">
                      {assignee.type === "auto"
                        ? "Auto-assigned by the bot"
                        : claimedAtLabel
                          ? `Claimed ${claimedAtLabel}`
                          : "Claimed"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void onReleaseThread?.(tid)}
                  className="shrink-0 rounded-md border border-indigo-200 bg-background px-2 py-1 text-[10.5px] font-semibold text-indigo-700 transition hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-indigo-500/40 dark:text-indigo-300 dark:hover:bg-indigo-950/60"
                >
                  Release
                </button>
              </div>
            ) : null}
          </section>

          <section>
            <SnoozeMenu
              align="start"
              onSnooze={(until) => void onSnoozeThread?.(tid, until)}
            >
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-left transition hover:bg-amber-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-amber-500/30 dark:bg-amber-950/30 dark:hover:bg-amber-950/50"
              >
                <span className="flex items-center gap-2 text-xs font-semibold text-amber-800 dark:text-amber-200">
                  <Clock className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                  Snooze this thread
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              </button>
            </SnoozeMenu>
          </section>
          */}

          <section>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Checklist
                {tasks.length > 0 ? (
                  <>
                    {" · "}
                    <span className="text-indigo-600 dark:text-indigo-400">{openTasks.length} open</span>
                  </>
                ) : null}
              </p>
              {doneTasks.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setHideDone((prev) => !prev)}
                  className="text-[10.5px] font-semibold text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {hideDone ? `Show done (${doneTasks.length})` : "Collapse done"}
                </button>
              ) : null}
            </div>
            {tasksLoading && tasks.length === 0 ? (
              <div className="space-y-1.5">
                <Skeleton className="h-14 w-full rounded-lg" />
                <Skeleton className="h-14 w-full rounded-lg" />
              </div>
            ) : (
              <div className="space-y-1.5">
                {openTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    nowMs={nowMs}
                    assignableUsers={assignableUsers}
                    onToggle={onTaskToggle}
                    onClaim={onTaskClaim}
                    onAssign={onTaskAssign}
                    onDelete={onTaskDelete}
                  />
                ))}
                {!hideDone
                  ? doneTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        nowMs={nowMs}
                        onToggle={onTaskToggle}
                        onDelete={onTaskDelete}
                      />
                    ))
                  : null}
                {tasks.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border px-3 py-3 text-center text-[11px] text-muted-foreground">
                    No tasks yet — locking a thread creates a “Reply to patient” task automatically.
                  </p>
                ) : null}
              </div>
            )}
            {adding ? (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
                <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <input
                  ref={addInputRef}
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitNewTask();
                    if (e.key === "Escape") {
                      setNewTitle("");
                      setAdding(false);
                    }
                  }}
                  onBlur={submitNewTask}
                  placeholder="What needs doing?"
                  aria-label="New task title"
                  className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="mt-2 flex w-full items-center gap-2 rounded-lg border border-dashed border-muted-foreground/40 px-3 py-2 text-left text-xs text-muted-foreground transition hover:border-foreground/40 hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Add a task…
              </button>
            )}
          </section>
        </div>
      </ScrollArea>
    </aside>
  );
}
